import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Autosuggest from 'react-autosuggest'
import debounce from 'lodash/debounce'
import { cx } from '@emotion/css'
import CloseIcon from '@offerzen/zenicon/CloseIcon'
import LoaderIcon from '@offerzen/zenicon/LoaderIcon'
import SearchIcon from '@offerzen/zenicon/SearchIcon'
import { colors } from '@offerzen/pinata-shame'
import AccessibleIcon from '../../../CandidateExperience/components/AccessibleIcon'
import FormErrorMessage from '../FormErrorMessage'
import styles, { wrapperTheme, wrapperError, itemTheme, autoSuggest } from './styles'

// TODO: replace with Pinata's SingleAutocomplete.
// There are some styling changes to smooth out between the two but otherwise very similar.
class SingleAutocomplete extends Component {
  static propTypes = {
    meta: PropTypes.object,
    input: PropTypes.object,
    name: PropTypes.string,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    readOnly: PropTypes.bool,
    tabIndex: PropTypes.string,
    autoFocus: PropTypes.bool,
    loading: PropTypes.bool,
    className: PropTypes.string,
    errorClassName: PropTypes.string, // Had to be added for onboarding. Should be removed when all onboarding is migrated
    theme: PropTypes.oneOf(['', 'primary', 'secondary', 'success', 'warning', 'error', 'legacy', 'dark', 'tertiary', 'info']),
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onSelect: PropTypes.func,
    data: PropTypes.array.isRequired,
    onDataFetch: PropTypes.func,
    onDataClear: PropTypes.func,
    normalizer: PropTypes.func,
    id: PropTypes.string,
  }

  static defaultProps = {
    meta: {},
    input: {},
    name: '',
    value: '',
    tabIndex: '0',
    data: [],
  }

  state = {
    focused: false,
    fetchStarted: false,
  }

  renderInput = ({ errored, theme, ...inputProps }) => {
    const value = this.props.input.value || this.props.value
    const loading = (this.props.loading && value) || this.state.fetchStarted

    return (
      <div
        className={cx(
          styles.wrapper,
          wrapperTheme(theme, inputProps.readOnly, this.state.focused),
          errored && wrapperError(this.state.focused),
        )}>
        <input type="text" {...inputProps} className={styles.input} role="presentation" autoFocus={this.props.autoFocus} />

        <div className={cx(styles.icon, !!value && styles.iconClear, loading && styles.iconSpinning)} onClick={this.clearInput}>
          <AccessibleIcon
            icon={(loading && LoaderIcon) || (value ? CloseIcon : SearchIcon)}
            size={18}
            color={errored ? colors.mandy : colors.grayChateau}
          />
        </div>
      </div>
    )
  }

  renderBody = ({ containerProps, children }) => {
    const value = this.props.input.value || this.props.value

    if (!this.state.focused || !children || !value || this.state.fetchStarted) return null

    return (
      <ul {...containerProps} className={styles.body}>
        {children}
      </ul>
    )
  }

  renderItem = (suggestion, { isHighlighted }) => (
    <span className={cx(styles.item, isHighlighted && itemTheme(this.props.theme))}>{this.getNormalizer()(suggestion)}</span>
  )

  onInputChange = (_, { newValue, method }) => {
    if (method === 'up' || method === 'down') {
      this.setState({ fetchStarted: false })
      return
    }

    if (method === 'escape' || method === 'enter') {
      this.setState({ fetchStarted: false })
      if (this.props.onDataClear) this.props.onDataClear()
      if (this.input) this.input.blur()
      return
    }

    this.setState({ fetchStarted: !!newValue })

    const handler = this.props.input.onChange || this.props.onChange
    if (handler) handler(newValue)

    if (!newValue && this.props.onDataClear) this.props.onDataClear()
  }

  onInputFocus = ev => {
    const handler = this.props.input.onFocus || this.props.onFocus
    if (handler) handler(ev)

    this.setState({ focused: true })
  }

  onInputBlur = () => {
    const handler = this.props.input.onBlur || this.props.onBlur
    if (handler) handler()

    this.setState({ focused: false })
  }

  clearInput = ev => {
    if (this.props.readOnly) return

    this.setState({ fetchStarted: false })

    ev.stopPropagation()

    const handler = this.props.input.onChange || this.props.onChange
    if (handler) handler('')

    if (this.props.onDataClear) this.props.onDataClear()
    if (this.input) this.input.focus()
  }

  getNormalizer = () => this.props.normalizer || (suggestion => suggestion.label)

  fetchData = debounce(({ reason, value }) => {
    if ((reason === 'input-changed' || reason === 'input-focused') && value) {
      if (this.props.onDataFetch) this.props.onDataFetch(value)
      this.setState({ fetchStarted: false })
    }
  }, 150)

  selectSuggestion = (_, { suggestion }) => {
    this.setState({ fetchStarted: false })

    const blurHandler = this.props.input.onBlur || this.props.onBlur
    if (blurHandler) blurHandler()

    if (this.props.onSelect) this.props.onSelect(suggestion)
  }

  render() {
    const { meta, input, theme, ...props } = this.props
    const value = input.value || props.value
    const errored = meta.touched && meta.error

    return (
      <div className={cx(styles.container, props.className, errored && props.errorClassName)}>
        <Autosuggest
          highlightFirstSuggestion
          alwaysRenderSuggestions
          ref={autosuggest => {
            if (autosuggest) this.input = autosuggest.input
          }}
          focusInputOnSuggestionClick={false}
          suggestions={props.data}
          onSuggestionsFetchRequested={this.fetchData}
          getSuggestionValue={suggestion => this.getNormalizer()(suggestion)}
          onSuggestionSelected={this.selectSuggestion}
          renderInputComponent={this.renderInput}
          renderSuggestion={this.renderItem}
          renderSuggestionsContainer={this.renderBody}
          theme={autoSuggest}
          inputProps={{
            value,
            theme,
            errored,
            placeholder: props.placeholder,
            readOnly: props.readOnly,
            tabIndex: props.tabIndex,
            onChange: this.onInputChange,
            onFocus: this.onInputFocus,
            onBlur: this.onInputBlur,
            id: props.id,
          }}
        />
        <If condition={errored}>
          <FormErrorMessage text={meta.error} />
        </If>
      </div>
    )
  }
}

export default SingleAutocomplete
